import { useMemo } from "react";
import type { CoachItemReach } from "@/lib/types";
import { useCoachEngagement } from "./useCoachEngagement";

/**
 * Per-item reach, keyed for O(1) lookup from a list row.
 *
 * The Library pages render every note or clip the team has, so a linear
 * scan per row would be quadratic on a busy team. The engagement query
 * is already scope-keyed and cached by TanStack Query, so calling this
 * from both pages costs one request, not two.
 *
 * Returns a `get(kind, id)` rather than the raw array so a caller can't
 * accidentally render the whole reach set.
 */
export function useItemReach() {
  const engagement = useCoachEngagement({});
  const byKey = useMemo(() => {
    const map = new Map<string, CoachItemReach>();
    for (const row of engagement.data?.item_reach ?? []) {
      map.set(`${row.kind}:${row.item_id}`, row);
    }
    return map;
  }, [engagement.data?.item_reach]);

  return {
    get: (kind: "note" | "clip", id: number) => byKey.get(`${kind}:${id}`),
    /** True until the first payload lands, so a row can hold the chip
     * back rather than flashing "Seen 0/0" on every item. */
    isLoading: engagement.isLoading,
  };
}
